<template>
  <div class="checkout-container">
    <ValidationObserver ref="form">
      <ul class="progress-bar grid-x grid-padding-x" v-show="currentStep > 0">
        <li class="cell small-12 _first" @click="goToStep(1)">
          <span class="progress-bar-item" :class="{'_active': currentStep === 1, '_complete': currentStep > 1}">{{ $t('Address and shipping') }}</span>
        </li>
        <li class="cell small-12 _last" @click="goToStep(3)">
          <span class="progress-bar-item _last" :class="{'_active': (currentStep === 3 || currentStep === 4)}">{{ $t('Payment') }}</span>
        </li>
      </ul>
      <div class="checkout-error-container page messages" v-if="inlineCheckoutError">
        <span v-html="inlineCheckoutError" class="message error"></span>
      </div>
      <div class="checkout-error-container page messages" v-if="sessionErrorMessage">
        <span v-html="sessionErrorMessage" class="message error"></span>
      </div>
      <div class="checkout-error-container page messages" v-if="sessionMergeMessage">
        <span class="message warning" v-html="sessionMergeMessage"></span>
      </div>
      <div class="checkout-steps-container">
        <form action="/checkout" method="post" @submit.prevent.stop>
          <div class="checkout-address-layout grid-x grid-padding-x" v-if="currentStep === 1">
            <div class="cell small-24 large-14">
              <ValidationObserver>
                <checkout-address-detail
                  :addresses="addresses"
                  :is-logged-in="isLoggedIn"
                  :is-business-account="isBusinessAccount"
                ></checkout-address-detail>
              </ValidationObserver>
            </div>
            <div class="cell small-24 large-10">
              <checkout-shipping-detail></checkout-shipping-detail>
            </div>
          </div>
          <div class="checkout-payment-layout grid-x grid-padding-x" v-if="currentStep === 3">
            <div class="cell small-24 large-14">
              <ValidationObserver>
                <checkout-payment-detail></checkout-payment-detail>
                <checkout-coupon-form></checkout-coupon-form>
              </ValidationObserver>
            </div>
            <div class="cell small-24 large-10 checkout-summary">
              <checkout-summary></checkout-summary>
            </div>
          </div>
        </form>
      </div>
      <div class="checkout-footer">
        <a href="/checkout/cart/" class="back">&laquo;&nbsp;{{ $t('Back to cart') }}</a>
        <button type="button" class="_button _cta _chevron" v-show="currentStep === 1" @click="goToStep(3)">{{ $t('To payment') }}</button>
        <button type="submit" class="_button _cta _chevron" :class="{'_loading': loadingCheckout}" v-show="currentStep === 3 || currentStep === 4" :disabled="loadingCheckout" @click="validatedPlaceOrder">{{ $t('Complete order') }}</button>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
  import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
  import ServiceContainer from '@flashpointbv/solar-service-container';
  import {ValidationObserver, validate} from 'vee-validate';
  import VueI18n from 'vue-i18n';
  import TranslateResult = VueI18n.TranslateResult;
  import {
    EcommerceItem,
    trackAddPaymentInfo,
    trackAddShippingInfo,
  } from '../../../shared/helpers/checkout-ecommerce';
  import {checkoutGrandTotal, needsHostedPayment} from '../../../shared/helpers/checkout-free-order';
  import {KlaviyoTracking} from '../../../shared/helpers/klaviyo';

  @Component({
    name: 'checkout-checkout',
    components: {
      'checkout-address-detail': ServiceContainer().getComponent('checkout-address-detail'),
      'checkout-shipping-detail': ServiceContainer().getComponent('checkout-shipping-detail'),
      'checkout-payment-detail': ServiceContainer().getComponent('checkout-payment-detail'),
      'checkout-summary': ServiceContainer().getComponent('checkout-summary'),
      'checkout-coupon-form': ServiceContainer().getComponent('checkout-coupon-form'),
      ValidationObserver
    }
  })

  export default class extends Vue {
    $refs!: {
      form: InstanceType<typeof ValidationObserver>;
    }

    @Prop({ required: false, default: null }) private mergeMessage: string | null;
    @Prop({ required: true }) private quote: object;
    @Prop({ default: false }) private isLoggedIn: boolean;
    @Prop({ default: false }) private isBusinessAccount: boolean;
    @Prop({ default: null }) private customerEmail: string;
    @Prop({ default: [] }) private addresses: Array<object>;
    @Prop({ default: null }) private countries: Array<object>;
    @Prop({ default: null }) private errorMessage: string | null;

    private loadingCheckout: boolean = false;
    private checkoutErrorMessage: TranslateResult = '';
    private lastIdentifiedEmail: string = '';

    private get inlineCheckoutError(): string {
      return String(this.checkoutErrorMessage || '').trim();
    }

    private get sessionErrorMessage(): string {
      return String(this.errorMessage || '').trim();
    }

    private get sessionMergeMessage(): string {
      return String(this.mergeMessage || '').trim();
    }

    private get currentStep(): number {
      return this.$store.getters['CheckoutGlobal/currentStep'];
    }

    private get quoteItemCount(): number {
      return this.quote?.['items_qty'] ?? 0;
    }

    private get selectedPaymentMethod(): object {
      return this.$store.getters['CheckoutPayment/selectedPaymentMethod'];
    }

    private get selectedPaymentIssuer(): object {
      return this.$store.getters['CheckoutPayment/selectedPaymentIssuer'];
    }

    private get checkoutItems(): Array<EcommerceItem> {
      const quoteItems = this.quote?.['items'];
      if (Array.isArray(quoteItems) && quoteItems.length) {
        return quoteItems;
      }

      const totalsItems = this.$store.getters['CheckoutTotals/cartItems'];
      return Array.isArray(totalsItems) ? totalsItems : [];
    }

    private get checkoutCoupon(): string {
      return String(
        this.quote?.['coupon_code']
        || this.$store.getters['CheckoutPayment/couponCode']
        || ''
      );
    }

    private get checkoutValue(): number {
      return checkoutGrandTotal(
        this.$store.getters['CheckoutTotals/cartTotals']?.['grand_total'],
        this.quote?.['grand_total']
      );
    }

    private get shippingTier(): string {
      const method = this.$store.getters['CheckoutShipping/shippingMethod'] || {};
      return String(method['method_title'] || method['carrier_title'] || method['carrier_code'] || '');
    }

    private get paymentType(): string {
      const method = this.selectedPaymentMethod || {};
      return String(method['title'] || method['code'] || '');
    }

    private get storeCustomerEmail(): string {
      return String(this.$store.getters['CheckoutGlobal/customerEmail'] || '');
    }

    private created(): void {
      this.$store.commit('CheckoutAddress/SET_CUSTOMER_CONTEXT', {
        isLoggedIn: Boolean(this.isLoggedIn),
        addressIds: (this.addresses || []).map((address) => address['id'] || address['entity_id']),
      });

      if (this.quoteItemCount < 1) {
        window.location.replace('/checkout/cart');
      }

      const email = (this.customerEmail || '').trim();
      if (this.isLoggedIn || email) {
        this.$store.commit('CheckoutGlobal/SET_CUSTOMER_EMAIL', email);
      }
      this.syncCheckoutIdentity(this.storeCustomerEmail);

      if (this.isLoggedIn) {
        this.$store.commit('CheckoutGlobal/SET_IS_BUSINESS_ACCOUNT', this.isBusinessAccount);
      }

      this.$store.dispatch('CheckoutShipping/setShippingCountries', this.countries ?? []);

      window.addEventListener('popstate', () => {
        this.goToStep(window.location.hash === '#Betaling' ? 3 : 1);
      });
    }

    @Watch('storeCustomerEmail')
    private onStoreCustomerEmail(email: string): void {
      this.syncCheckoutIdentity(email);
    }

    private async syncCheckoutIdentity(email: string): Promise<void> {
      const normalized = (email || '').trim();
      if (!normalized || !normalized.includes('@') || normalized === this.lastIdentifiedEmail) {
        return;
      }

      this.lastIdentifiedEmail = normalized;
      KlaviyoTracking.identify(normalized);

      try {
        const {data} = await this.$solarClient.post('/api/checkout/customers/email', {email: normalized});
        if (data?.masked_quote_id) {
          window['solarQuoteIdMask'] = data.masked_quote_id;
        }
      } catch {
        // Quote email write must not block checkout.
      }
    }

    // Address and payment steps both use a two-column layout.
    @Watch('currentStep', { immediate: true })
    private onStepChange(step: number): void {
      document.body.classList.toggle('_checkout-wide', step === 1 || step >= 3);
      document.body.classList.toggle('_checkout-payment', step >= 3);
    }

    private async mounted(): Promise<void> {
      await this.$store.dispatch('CheckoutTotals/fetchCartTotals');

      const cartTotals = this.$store.getters['CheckoutTotals/cartTotals'];
      if (0 === cartTotals?.['extension_attributes']?.['valid_stock']) {
        window.location.href = '/checkout/cart';
      }
    }

    private goToStep(step): void {
      if (step === this.currentStep) {
        return;
      }

      this.$refs.form.validate().then(async success => {
        if (success || step < this.currentStep) {
          if (this.currentStep === 1 && step > 1) {
            const isPostnl = this.$store.getters['CheckoutShipping/isPostnlShippingSelected'];
            if (isPostnl) {
              const mode = this.$store.getters['CheckoutShipping/postnlMode'];
              const option = this.$store.getters['CheckoutShipping/postnlDeliveryOption'];
              const hasValidOption = mode === 'pickup'
                ? option?.type === 'pickup' && !!option?.LocationCode
                : option?.type === 'delivery' && !!option?.date;

              if (!hasValidOption) {
                const message = mode === 'pickup'
                  ? 'Please choose a pickup point.'
                  : 'Please choose a delivery date.';
                this.$store.commit('CheckoutShipping/SET_POSTNL_ERROR', message);
                this.scrollToPostnlOptions();
                return;
              }

              const saved = await this.$store.dispatch('CheckoutShipping/savePostnlDeliveryOption');
              if (!saved) {
                this.$store.commit(
                  'CheckoutShipping/SET_POSTNL_ERROR',
                  mode === 'pickup' ? 'Please choose a pickup point.' : 'Please choose a delivery date.'
                );
                this.scrollToPostnlOptions();
                return;
              }
            }
          }

          if (this.currentStep === 1 && step > 1) {
            trackAddShippingInfo(this.checkoutItems, this.checkoutValue, this.shippingTier, this.checkoutCoupon);
          }

          this.$store.dispatch('CheckoutGlobal/gotoStep', step);
          window.scrollTo(0, 0);
          return;
        }

        // Validation failed while moving forward — bring the first error into view.
        if (step > this.currentStep) {
          this.$nextTick(() => {
            const target = document.querySelector('.checkout-container .field-error, .postnl-delivery-options .field-error, .postnl-delivery-options');
            if (target) {
              target.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
          });
        }
      });
    }

    private scrollToPostnlOptions(): void {
      this.$nextTick(() => {
        const target = document.querySelector('.postnl-delivery-options');
        if (target) {
          target.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      });
    }

    private async validatedPlaceOrder(): Promise<void> {
      this.$refs.form.validate().then(success => {
        if (success) {
          return this.placeOrder();
        }
      });
    }

    private async placeOrder(): Promise<any> {
      const methodCode = this.selectedPaymentMethod?.['code'] ?? '';

      trackAddPaymentInfo(this.checkoutItems, this.checkoutValue, this.paymentType, this.checkoutCoupon);

      this.loadingCheckout = true;
      try {
        const {data} = await this.$store.dispatch('CheckoutPayment/placeOrder');

        if (needsHostedPayment(this.checkoutValue, methodCode) && data?.hasOwnProperty('RequiredAction')) {
          window.location.replace(data['RequiredAction']['RedirectURL']);
          return;
        }

        if (needsHostedPayment(this.checkoutValue, methodCode) && methodCode.includes('paynl_')) {
          window.location.replace('/paynl/checkout/redirect');
          return;
        }

        // €0 / Magento "free" has no hosted payment; asking MultiSafepay 502s.
        if (needsHostedPayment(this.checkoutValue, methodCode)) {
          // MultiSafepay: fetch the hosted payment URL via Solar (the Magento
          // connect/redirect controller requires a browser session we no longer have).
          try {
            const {data: payment} = await this.$solarClient.get('/api/checkout/payment/multisafepay/payment-url');

            if (payment?.url) {
              window.location.replace(payment.url);
              return;
            }

            this.checkoutErrorMessage = this.$t('Unable to start the payment, please try again later.');
          } catch (paymentErr) {
            this.checkoutErrorMessage = this.extractApiErrorMessage(
              paymentErr,
              String(this.$t('Unable to start the payment, please try again later.'))
            );
          }

          await this.$store.dispatch('CheckoutPayment/collectPaymentMethods');
          this.loadingCheckout = false;
          window.scrollTo(0, 0);
          return;
        }

        window.location.assign('/checkout/onepage/success');
      } catch (err) {
        this.checkoutErrorMessage = this.extractApiErrorMessage(
          err,
          String(this.$t('Unable to place order, please try again later.'))
        );
        window.scrollTo(0, 0);
        this.loadingCheckout = false;
      }
    }

    private extractApiErrorMessage(err: any, fallback: string): string {
      try {
        const raw = err?.request?.response || err?.response?.data;
        const response = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const message = response?.exception?.message || response?.message || response?.error;

        if (typeof message === 'string' && message.trim() !== '') {
          return message;
        }
      } catch (e) {
        // Fall through to default message.
      }

      if (err?.message && typeof err.message === 'string' && err.message !== 'Network Error') {
        return err.message;
      }

      return fallback;
    }
  }
</script>
